import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Shopify Ajax product JSON uses the smallest currency unit (e.g. cents) for whole numbers.
 * Some responses use decimal strings ("49.99") in major units — detect and convert consistently.
 * @param {string|number|null|undefined} raw
 * @returns {number}
 */
/**
 * BCP 47 locale for Intl (o.a. komma als decimaal in nl/de).
 * @param {string} [raw] data-number-locale (Shopify request.locale.iso_code)
 */
/**
 * @param {any} product
 * @returns {boolean}
 */
function isHiddenGiftProduct(product) {
  const tags = product?.tags;
  if (Array.isArray(tags)) return tags.includes('hidden-gift');
  if (typeof tags === 'string') {
    return tags.split(',').some((tag) => tag.trim() === 'hidden-gift');
  }
  return false;
}

function resolveIntlLocale(raw) {
  const s = raw?.trim().replace(/_/g, '-');
  if (s) {
    const lower = s.toLowerCase();
    if (lower === 'nl') return 'nl-NL';
    if (lower === 'nl-be') return 'nl-BE';
    return s;
  }
  const htmlLang = document.documentElement.getAttribute('lang')?.trim().replace(/_/g, '-');
  if (htmlLang) {
    const lower = htmlLang.toLowerCase();
    if (lower === 'nl') return 'nl-NL';
    if (lower === 'nl-be') return 'nl-BE';
    return htmlLang;
  }
  return undefined;
}

/**
 * EU (en o.a. NL) gebruiken komma als decimaal; sommige Intl-implementaties geven nog een punt.
 * Laat strings met een komma ongemoeid (bijv. 1.234,56).
 */
function forceEuDecimalComma(formatted, localeTag) {
  const loc = (localeTag || '').toLowerCase();
  const lang = loc.split('-')[0] || '';
  if (!/^(nl|de|fr|es|it|pt|pl|sk|sl|hr|el|fi|cs|ro|bg|hu)$/.test(lang)) return formatted;
  if (formatted.includes(',') || !formatted.includes('.')) return formatted;
  return formatted.replace(/(\d)\.(\d{2})(?!\d)/g, '$1,$2');
}

/**
 * Zelfde titel = zelfde “product” (bijv. kleur als apart Shopify-product); max één in de carousel.
 * @param {string | null | undefined} title
 */
function normalizeTitleForDedupe(title) {
  return String(title ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

const YAL_SESSION_SEED_KEY = 'kiki-yal-rec-session-seed';

/**
 * Stable per browser tab session; new tab/session gets a new seed.
 * @returns {number}
 */
function getYalSessionSeed() {
  try {
    const stored = sessionStorage.getItem(YAL_SESSION_SEED_KEY);
    if (stored) {
      const n = parseInt(stored, 10);
      if (!Number.isNaN(n)) return n >>> 0;
    }
    const seed = (Math.random() * 0xffffffff) >>> 0;
    sessionStorage.setItem(YAL_SESSION_SEED_KEY, String(seed));
    return seed;
  } catch {
    return (Date.now() * 0x9e3779b1) >>> 0;
  }
}

/**
 * @param {number} seed
 * @returns {() => number}
 */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Fisher–Yates shuffle with a deterministic seed (varies per session + product).
 * @template T
 * @param {T[]} items
 * @param {number} seed
 * @returns {T[]}
 */
function seededShuffle(items, seed) {
  const arr = items.slice();
  const rng = mulberry32(seed);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * @param {string} [productId]
 * @returns {number}
 */
function getYalShuffleSeed(productId) {
  const sessionSeed = getYalSessionSeed();
  const pid = parseInt(productId || '0', 10) || 0;
  return (sessionSeed ^ pid) >>> 0;
}

function parseShopifyMoneyAmount(raw) {
  if (raw == null || raw === '') return NaN;
  const s = String(raw).trim();
  if (/[.,]/.test(s)) {
    const normalized = s.replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isNaN(n) ? NaN : n;
  }
  const n = parseFloat(s);
  if (Number.isNaN(n)) return NaN;
  return n / 100;
}

/**
 * @typedef {HTMLElement & { dataset: DOMStringMap }} KikiYalRoot
 */

class KikiYoullAlsoLove extends HTMLElement {
  /** @type {boolean} */
  #dragging = false;
  /** @type {number} */
  #startX = 0;
  /** @type {number} */
  #startScroll = 0;
  /** @type {boolean} */
  #didDrag = false;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {(() => void) | null} */
  #syncScrollbar = null;
  /** @type {(() => void) | null} */
  #scrollbarCleanup = null;

  connectedCallback() {
    const track = this.querySelector('.kiki-yal__track');
    if (!(track instanceof HTMLElement)) return;

    this.#bindDragScroll(track);
    this.#bindCustomScrollbar(track);
    this.#bindQuickAdd();
    this.#shuffleServerRecommendations(track);
    this.#maybeLoadRecommendations();
  }

  disconnectedCallback() {
    this.#scrollbarCleanup?.();
    this.#scrollbarCleanup = null;
    this.#syncScrollbar = null;
    this.#abort?.abort();
  }

  /**
   * Altijd zichtbare scroll-indicator (macOS/iOS verbergen vaak de systeem-scrollbar).
   * @param {HTMLElement} track
   */
  #bindCustomScrollbar(track) {
    const bar = this.querySelector('[data-kiki-yal-scrollbar]');
    const thumb = bar?.querySelector('.kiki-yal__scrollbar-thumb');
    if (!(bar instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

    const sync = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 1) {
        bar.classList.add('kiki-yal__scrollbar--hidden');
        return;
      }
      bar.classList.remove('kiki-yal__scrollbar--hidden');
      const cw = track.clientWidth;
      const sw = track.scrollWidth;
      const thumbW = Math.max((cw / sw) * cw, 48);
      const maxLeft = Math.max(cw - thumbW, 0);
      const p = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
      thumb.style.width = `${thumbW}px`;
      thumb.style.transform = `translateX(${p * maxLeft}px)`;
    };

    this.#syncScrollbar = sync;
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(track);
    const mo = new MutationObserver(() => requestAnimationFrame(sync));
    mo.observe(track, { childList: true, subtree: true });
    sync();
    requestAnimationFrame(sync);
    queueMicrotask(sync);

    this.#scrollbarCleanup = () => {
      track.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      ro.disconnect();
      mo.disconnect();
    };
  }

  /**
   * @param {HTMLElement} track
   */
  #bindDragScroll(track) {
    const prefersReduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const onPointerDown = (e) => {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      const t = e.target;
      if (t instanceof Element && t.closest('[data-kiki-yal-quick], .kiki-yal__quick--link, a.kiki-yal__title-link')) {
        return;
      }
      this.#dragging = true;
      this.#didDrag = false;
      this.#startX = e.clientX;
      this.#startScroll = track.scrollLeft;
      track.setPointerCapture(e.pointerId);
      track.classList.add('kiki-yal__track--dragging');
    };

    const onPointerMove = (e) => {
      if (!this.#dragging) return;
      const dx = e.clientX - this.#startX;
      if (Math.abs(dx) > 4) this.#didDrag = true;
      track.scrollLeft = this.#startScroll - dx;
    };

    const onPointerUp = (e) => {
      if (!this.#dragging) return;
      this.#dragging = false;
      try {
        track.releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      track.classList.remove('kiki-yal__track--dragging');
    };

    const onClickCapture = (e) => {
      if (!this.#didDrag) return;
      const el = e.target;
      if (el instanceof Element && el.closest('a')) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.#didDrag = false;
    };

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('click', onClickCapture, true);

    if (!prefersReduced) {
      track.style.scrollBehavior = 'smooth';
    }
  }

  #bindQuickAdd() {
    this.addEventListener('click', (e) => {
      const btn = e.target instanceof Element ? e.target.closest('[data-kiki-yal-quick]') : null;
      if (!(btn instanceof HTMLButtonElement)) return;
      if (btn.disabled) return;
      const card = btn.closest('.kiki-yal__card');
      if (!(card instanceof HTMLElement)) return;
      const variantId = card.dataset.variantId;
      if (!variantId) return;
      e.preventDefault();
      e.stopPropagation();
      this.#addToCart(btn, variantId);
    });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} variantId
   */
  async #addToCart(button, variantId) {
    const cartUrl = /** @type {any} */ (window).Theme?.routes?.cart_add_url;
    if (!cartUrl) return;

    button.disabled = true;
    button.classList.add('kiki-yal__quick--loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const cartSectionComponents = document.querySelectorAll('cart-items-component, custom-cart-drawer-component');
    const sectionIds = [];
    cartSectionComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(cartUrl, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const data = await response.json();

      if (data.status) {
        console.warn('Cart add error', data.message);
        return;
      }

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'kiki-youll-also-love',
          variantId,
          sections: data.sections,
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      button.disabled = false;
      button.classList.remove('kiki-yal__quick--loading');
    }
  }

  /**
   * Shopify recommendations are stable per product; reorder server-rendered cards per session.
   * @param {HTMLElement} track
   */
  #shuffleServerRecommendations(track) {
    if (this.dataset.sourceType !== 'recommendations') return;
    if (/** @type {any} */ (window).Shopify?.designMode) return;
    const cards = [...track.querySelectorAll('.kiki-yal__card:not(.kiki-yal__card--placeholder)')];
    if (cards.length < 2) return;

    const seed = getYalShuffleSeed(this.dataset.productId);
    const order = seededShuffle(
      cards.map((_, i) => i),
      seed
    );
    const frag = document.createDocumentFragment();
    for (const i of order) {
      frag.appendChild(cards[i]);
    }
    track.appendChild(frag);
    this.#syncScrollbar?.();
  }

  async #maybeLoadRecommendations() {
    if (this.dataset.loadRecommendations !== 'true') return;
    const track = this.querySelector('.kiki-yal__track');
    if (!(track instanceof HTMLElement)) return;
    if (track.querySelector('.kiki-yal__card:not(.kiki-yal__card--placeholder)')) return;

    const productId = this.dataset.productId;
    const max = this.dataset.maxProducts || '8';
    const intent = this.dataset.intent || 'related';
    const excludeId = this.dataset.excludeProductId || '';
    if (!productId || productId === '0') return;

    this.#abort?.abort();
    this.#abort = new AbortController();

    const url = new URL(`${window.location.origin}/recommendations/products.json`);
    url.searchParams.set('product_id', productId);
    // Shopify max = 10; grotere pool zodat na titel-dedupe nog genoeg andere producten overblijven.
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);

    try {
      const res = await fetch(url.toString(), { signal: this.#abort.signal });
      if (!res.ok) throw new Error(String(res.status));
      const json = await res.json();
      const rawProducts = /** @type {any[]} */ (json.products || []);
      const shuffleSeed = /** @type {any} */ (window).Shopify?.designMode
        ? 0
        : getYalShuffleSeed(productId);
      const products =
        shuffleSeed === 0 ? rawProducts : seededShuffle(rawProducts, shuffleSeed);
      const frag = document.createDocumentFragment();
      const currency = this.dataset.currency || /** @type {any} */ (window).Shopify?.currency?.active || 'USD';
      let count = 0;
      const desired = parseInt(max, 10) || 8;
      const seenTitles = new Set();

      for (const p of products) {
        if (isHiddenGiftProduct(p)) continue;
        if (excludeId && String(p.id) === String(excludeId)) continue;
        const titleKey = normalizeTitleForDedupe(p.title);
        if (seenTitles.has(titleKey)) continue;
        seenTitles.add(titleKey);
        const el = this.#buildCardFromJson(p, currency);
        if (el) {
          frag.appendChild(el);
          count++;
          if (count >= desired) break;
        }
      }

      track.innerHTML = '';
      track.appendChild(frag);
      this.classList.toggle('kiki-yal--empty', count === 0);
      this.#syncScrollbar?.();
      requestAnimationFrame(() => this.#syncScrollbar?.());
    } catch (e) {
      if (/** @type {any} */ (e)?.name === 'AbortError') return;
      console.error('kiki-youll-also-love recommendations', e);
      this.classList.add('kiki-yal--empty');
    }
  }

  /**
   * @param {any} p
   * @param {string} currency
   * @returns {HTMLElement | null}
   */
  #buildCardFromJson(p, currency) {
    const v0 = p.variants?.[0];
    if (!v0) return null;

    const variants = p.variants || [];
    const singleVariant = variants.length === 1;
    const productAvailable = typeof p.available === 'boolean' ? p.available : true;
    const vAvail = v0.available !== false;

    const showQuick = this.dataset.showQuick !== 'false';
    let quickMode = 'hidden';
    if (showQuick) {
      if (!productAvailable || (singleVariant && !vAvail)) {
        quickMode = 'disabled';
      } else if (singleVariant && vAvail) {
        quickMode = 'ajax';
      } else if (productAvailable) {
        quickMode = 'link';
      } else {
        quickMode = 'disabled';
      }
    }

    const priceNum = parseShopifyMoneyAmount(v0.price ?? p.price);
    const compareNum =
      v0.compare_at_price != null && v0.compare_at_price !== ''
        ? parseShopifyMoneyAmount(v0.compare_at_price)
        : NaN;
    const onSale = !Number.isNaN(compareNum) && !Number.isNaN(priceNum) && compareNum > priceNum;

    let intlLocale =
      resolveIntlLocale(this.dataset.numberLocale) ??
      resolveIntlLocale(document.documentElement.getAttribute('lang') ?? undefined);
    if (!intlLocale) {
      const hl = document.documentElement.getAttribute('lang')?.toLowerCase() ?? '';
      if (hl.startsWith('nl')) intlLocale = 'nl-NL';
    }
    const localeForIntl = intlLocale ?? resolveIntlLocale(this.dataset.numberLocale);
    const fmt = (n) => {
      const v = Number.isNaN(n) ? 0 : n;
      const raw = new Intl.NumberFormat(localeForIntl, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);
      return forceEuDecimalComma(raw, localeForIntl ?? this.dataset.numberLocale);
    };

    const article = document.createElement('article');
    article.className = 'kiki-yal__card';
    article.dataset.productId = String(p.id);
    article.dataset.variantId = String(v0.id);
    article.dataset.quick = quickMode;

    const imgUrl = (() => {
      const fi = p.featured_image;
      if (typeof fi === 'string') {
        return fi.startsWith('http') || fi.startsWith('//') ? (fi.startsWith('//') ? `https:${fi}` : fi) : `https:${fi}`;
      }
      if (fi && typeof fi === 'object' && fi.url) {
        const u = String(fi.url);
        return u.startsWith('http') || u.startsWith('//') ? (u.startsWith('//') ? `https:${u}` : u) : u;
      }
      const im = p.image;
      if (im && typeof im === 'object' && im.src) {
        const u = String(im.src);
        return u.startsWith('http') || u.startsWith('//') ? (u.startsWith('//') ? `https:${u}` : u) : u;
      }
      return '';
    })();

    const media = document.createElement('div');
    media.className = 'kiki-yal__media';

    const imgLink = document.createElement('a');
    imgLink.className = 'kiki-yal__img-link';
    imgLink.href = p.url || `/products/${p.handle}`;
    imgLink.tabIndex = -1;
    imgLink.setAttribute('aria-hidden', 'true');

    const bg = document.createElement('span');
    bg.className = 'kiki-yal__img-bg';
    if (imgUrl) {
      const im = document.createElement('img');
      im.className = 'kiki-yal__img';
      im.loading = 'lazy';
      im.alt = p.title || '';
      im.src = imgUrl;
      bg.appendChild(im);
    } else {
      const ph = document.createElement('span');
      ph.className = 'kiki-yal__img kiki-yal__img--placeholder';
      bg.appendChild(ph);
    }
    imgLink.appendChild(bg);
    media.appendChild(imgLink);

    const cartSvg =
      '<span class="kiki-yal__quick-inner" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="20" height="20"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"/></svg></span>';

    if (showQuick) {
      if (quickMode === 'ajax') {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'kiki-yal__quick';
        b.setAttribute('data-kiki-yal-quick', '');
        const addLabel = this.dataset.labelAdd || 'Add to cart';
        b.setAttribute('aria-label', `${addLabel} — ${p.title || ''}`);
        b.innerHTML = cartSvg;
        media.appendChild(b);
      } else if (quickMode === 'link') {
        const a = document.createElement('a');
        a.href = p.url || `/products/${p.handle}`;
        a.className = 'kiki-yal__quick kiki-yal__quick--link';
        a.setAttribute('aria-label', `${this.dataset.labelChoose || 'Choose options'} — ${p.title || ''}`);
        a.innerHTML = cartSvg;
        media.appendChild(a);
      } else if (quickMode === 'disabled') {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'kiki-yal__quick';
        b.disabled = true;
        b.setAttribute('data-kiki-yal-quick', '');
        b.setAttribute('aria-label', `${this.dataset.labelSoldOut || 'Sold out'} — ${p.title || ''}`);
        b.innerHTML = cartSvg;
        media.appendChild(b);
      }
    }

    const titleA = document.createElement('a');
    titleA.href = p.url || `/products/${p.handle}`;
    titleA.className = 'kiki-yal__title-link';
    const h3 = document.createElement('h3');
    h3.className = 'kiki-yal__title';
    h3.textContent = p.title || '';
    titleA.appendChild(h3);

    const priceRow = document.createElement('div');
    priceRow.className = 'kiki-yal__price-row';
    if (onSale) {
      const c = document.createElement('span');
      c.className = 'kiki-yal__compare';
      c.textContent = fmt(compareNum);
      priceRow.appendChild(c);
    }
    const pr = document.createElement('span');
    pr.className = 'kiki-yal__price' + (onSale ? ' kiki-yal__price--sale' : '');
    pr.textContent = fmt(priceNum);
    priceRow.appendChild(pr);

    article.appendChild(media);
    article.appendChild(titleA);
    article.appendChild(priceRow);

    return article;
  }
}

if (!customElements.get('kiki-youll-also-love')) {
  customElements.define('kiki-youll-also-love', KikiYoullAlsoLove);
}
